import { isString, peek } from '@art-suite/art-core-ts';

import { ApiOrgProperty, ApiPropertyProject, ApiScan } from '@kaiizen/kai-pro-api';
import { ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { useScan } from '../apiScanAndTemplateState';
import { usePhotoMediumSizeUri } from '../contexts';
import { useOrgProperty } from './useOrgProperties';
import { usePropertyProject } from './usePropertyProjects';
import { usePropertyStreetView } from './usePropertyStreetView';

export const usePropertyHeroForOrgProperty = (orgPropertyOrOrgPropertyId: ApiOrgProperty | string | undefined) => {
  // Only fetch when handed an id. Given the record, use it as-is - callers that
  // already have it (e.g. propertyProject.orgProperty) shouldn't fetch it again.
  const orgPropertyId = isString(orgPropertyOrOrgPropertyId) ? orgPropertyOrOrgPropertyId : undefined;
  const { orgProperty: fetchedOrgProperty } = useOrgProperty(orgPropertyId);
  const orgProperty = orgPropertyId ? fetchedOrgProperty : orgPropertyOrOrgPropertyId as ApiOrgProperty | undefined;
  const heroScanPhotoId = orgProperty?.heroScanPhotoId;
  const heroScanPhotoUri = usePhotoMediumSizeUri(heroScanPhotoId ?? undefined);

  const fetchStreetView = orgProperty && !heroScanPhotoId;
  const streetView = usePropertyStreetView(fetchStreetView ? orgProperty : undefined);
  return heroScanPhotoUri ?? streetView ?? undefined;
};

export const usePropertyHeroForPropertyProject = (propertyProjectOrPropertyProjectId: ApiPropertyProject | string | undefined) => {
  const { propertyProject } = usePropertyProject(propertyProjectOrPropertyProjectId);
  return usePropertyHeroForOrgProperty(propertyProject?.orgProperty ?? propertyProject?.orgPropertyId);
};

export const usePropertyHeroForScan = (scanOrScanId: ApiScan | string | undefined, propertyProject?: ApiPropertyProject) => {
  const { scan } = useScan(scanOrScanId);
  const heroScanPhotoId = scan?.heroScanPhotoId;
  const heroScanPhotoUri = usePhotoMediumSizeUri(heroScanPhotoId ?? undefined);
  const fetchProjectHero = scan && !heroScanPhotoId;
  const projectHero = usePropertyHeroForPropertyProject(
    fetchProjectHero ? (propertyProject && propertyProject.id === scan?.propertyProjectId) ? propertyProject : scan?.propertyProjectId : undefined,
  );

  return heroScanPhotoUri ?? projectHero;
};

/**
 * Returns the hero photo URI for a given scan. (the LAST photo in scanRoot's photos array is the current hero photo)
 * @param scanRoot - The scan root to get the hero photo URI for.
 * @param scanId - The id of the scan to get the hero photo URI for.
 * @returns The hero photo URI.
 */
export const usePropertyHeroForEditingScan = (scanRoot: ScanRootOptimized | undefined, scanId: string | undefined) => {
  const editingHeroPhotoId = peek(scanRoot?.photos)?.photoId;
  const scanHero = usePhotoMediumSizeUri(editingHeroPhotoId);
  const propertyImage = usePropertyHeroForScan(editingHeroPhotoId ? undefined : scanId);
  return scanHero ?? propertyImage;
};
